/**
 * /login — the only anonymous app route.
 *
 * Centered Mantine `Paper` card on the brand gradient. A "Sign in with
 * Microsoft" pill button drives MSAL.js `loginPopup` with the API scope so
 * `/me` works immediately on redirect.
 *
 * Entra-only (CLAUDE.md §3) — no password field, no "Forgot password?".
 * There is an optional **email** field, but it is only a `loginHint` that
 * pre-fills the Microsoft page; the app never checks it pre-auth (that would
 * leak which emails are registered). Whether the account is set up is decided
 * after Entra authenticates (ADR-0014).
 */

import { useEffect, useState } from "react";
import { Alert, Button, Image, Paper, Stack, Text, TextInput } from "@mantine/core";
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { BRAND_GRADIENT } from "@/app/theme";
import { returnPath } from "./returnTo";
import { useAuthConfig } from "./useAuthConfig";

const ENDED_NOTICE: Record<string, string> = {
  session_ended: "Your session has ended. Sign in again to continue.",
  idle_timeout: "You were signed out after 10 minutes without activity.",
  renewal_failed:
    "Your sign-in could not be renewed — your account may have changed. Sign in again.",
};

export function Login() {
  const { instance, accounts, inProgress } = useMsal();
  const config = useAuthConfig();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Optional — pre-fills the Microsoft sign-in page (MSAL `loginHint`). It is
  // NOT checked here: whether the account is set up for this app is decided
  // AFTER Entra authenticates (a post-auth 403), never via an anonymous
  // pre-auth lookup (that would leak which emails are registered). ADR-0014.
  const [email, setEmail] = useState("");
  // Why the last session ended, when the person did not choose it
  // (ADR-0046). Any of these needs a fresh sign-in at Microsoft, not a
  // silent reuse of the old one: `prompt=login`.
  const [params] = useSearchParams();
  const endedBecause = params.get("reason");
  const notice = endedBecause ? ENDED_NOTICE[endedBecause] : undefined;

  // MSAL serialises interactions through `inProgress`. Calling loginPopup
  // while another flow is mid-execution throws `interaction_in_progress`
  // and leaves the cache stuck — disable the button until MSAL is idle.
  const msalIdle = inProgress === InteractionStatus.None;
  // Home, or the page the person was trying to open (ADR-0070).
  const target = returnPath(useLocation().state);

  useEffect(() => {
    if (accounts.length > 0) {
      navigate(target, { replace: true });
    }
  }, [accounts, navigate, target]);

  const handleSignIn = async () => {
    setError(null);
    setBusy(true);
    try {
      await instance.loginPopup({
        scopes: config.scopes,
        loginHint: email.trim() || undefined,
        prompt: notice ? "login" : undefined,
      });
      navigate(target, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        boxSizing: "border-box",
        background: BRAND_GRADIENT,
      }}
    >
      <Paper
        shadow="xl"
        radius="lg"
        p="xl"
        style={{ maxWidth: 420, width: "100%", textAlign: "center" }}
      >
        <Stack gap="md" align="center">
          {/* The wordmark carries the product name itself, so the heading that
              used to repeat it is gone. Two files, not one: the mark is flat
              navy, which disappears into the dark card, and flat near-white,
              which disappears into the light one. `lightHidden`/`darkHidden`
              swap them in CSS, so there is no flash on load the way a
              JS-computed scheme would give. */}
          <Image
            src="/logo-daquma-navy.png"
            alt="DAQUMA"
            w={240}
            h="auto"
            fit="contain"
            darkHidden
          />
          <Image
            src="/logo-daquma-white.png"
            alt="DAQUMA"
            w={240}
            h="auto"
            fit="contain"
            lightHidden
          />
          <Stack gap={4} align="center">
            <Text size="sm" c="dimmed">
              Sign in with your work account.
            </Text>
            <Text size="xs" c="dimmed" maw={340}>
              Authentication is handled by your organisation's Microsoft Entra ID. No passwords are
              stored on this site.
            </Text>
          </Stack>
          {notice && (
            <Alert color="blue" variant="light" w="100%" role="status">
              {notice}
            </Alert>
          )}
          <TextInput
            label="Work email"
            placeholder="you@yourcompany.com"
            type="email"
            value={email}
            w="100%"
            disabled={busy}
            description="Optional — pre-fills the Microsoft sign-in page."
            onChange={(e) => setEmail(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && msalIdle && !busy) void handleSignIn();
            }}
          />
          <Button
            onClick={handleSignIn}
            disabled={busy || !msalIdle}
            loading={busy}
            color="brand"
            size="md"
            leftSection={<MicrosoftMark />}
            mt="sm"
          >
            {!msalIdle ? "Wait…" : "Sign in with Microsoft"}
          </Button>
          {error && (
            <Alert variant="light" color="red" title="Sign-in failed" w="100%">
              {error}
            </Alert>
          )}
        </Stack>
      </Paper>
    </div>
  );
}

function MicrosoftMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
      <rect x="0" y="0" width="10" height="10" fill="#F35325" />
      <rect x="13" y="0" width="10" height="10" fill="#81BC06" />
      <rect x="0" y="13" width="10" height="10" fill="#05A6F0" />
      <rect x="13" y="13" width="10" height="10" fill="#FFBA08" />
    </svg>
  );
}
